<ng-template #viewPromptTemplate>
	@if (selectedPrompt(); as selectedPrompt) {
	<div [libFlexRow]="isMobileView()" [libFlexRowGap]="'16px'">
		<div libFlexColumn [libFlexSize]="isMobileView() ? 'full' : undefined">
			<lib-button (click)="handleCopySelectedResponse()" [buttonType]="'primary'">Copy response</lib-button>
		</div>
		<div libFlexColumn [libFlexSize]="isMobileView() ? 'full' : undefined">
			<lib-button (click)="handleCopySelectedPrompt()" [buttonType]="'primaryLight'">Copy prompt</lib-button>
		</div>
	</div>
	<div class="push-top">
		<lib-message [type]="'info'" [title]="'Prompt'" [icon]="'question'">
			{{ selectedPrompt.prompt }}
		</lib-message>
	</div>
	<lib-card class="push-top">
		<div [innerHTML]="selectedPrompt.generatedText | textToHtml"></div>
	</lib-card>
	}
</ng-template>

<div [libFlexRow]="isMobileView()" [libFlexRowGap]="'16px'" [libFlexRowAlign]="'start'">
	<div libFlexColumn [libFlexSize]="isMobileView() ? 'full' : 'fill'">
		@if (!promptResponse()) {
		<div>
			<div>
				<lib-card>
					<div [libFlexRow]="isMobileView()" [libFlexRowGap]="'16px'">
						@if (clientContextToUse(); as clientContextToUse) {
						<div libFlexColumn [libFlexSize]="isMobileView() ? 'full' : 'fill'">
							<div libFlexRow [libFlexRowAlign]="'start'">
								@if (clientContextToUse.avatar) {
								<div libFlexColumn [libFlexSize]="'50px'">
									<lib-image
										[image]="clientContextToUse.avatar"
										[imageSize]="'250px'"
										[isResponsive]="true"
										[alt]="'Client avatar'"
									></lib-image>
								</div>
								}
								<div libFlexColumn>
									<div libText2>{{ clientContextToUse.fullName }}</div>
									<div libText3>The Ai has context about this client to give you better answers.</div>
									@if (showEditClientContext()) {
									<div class="push-top-xs">
										<a class="w-no-underline" (click)="openClientContextSelector()"
											>Change client</a
										>
									</div>
									}
								</div>
							</div>
							<lib-message
								[title]="'🤖 Smarter Messaging with AI'"
								class="push-top"
								[type]="'tip'"
								[dismissId]="'clientContextInfo'"
							>
								<p>
									You can now use our built-in AI assistant to help you write quick, thoughtful
									messages to your clients — all based on their unique context, including workouts,
									nutrition, progress, and more.
								</p>

								<p>Just type a prompt like:</p>
								<ul>
									<li>“Summarize the progress of the client”</li>
									<li>“Respond to the latest message saying that I'll look into it this evening”</li>
									<li>“Suggest improvements to the client’s workout based on their goal”</li>
								</ul>

								<p>
									The AI will generate a clear, personalized message you can send as-is or fine-tune
									to match your tone.
								</p>

								<p>
									It's a simple way to save time, stay consistent, and keep communication personal and
									effective.
								</p>
							</lib-message>
						</div>
						} @else {
						<div libFlexColumn [libFlexSize]="isMobileView() ? 'full' : 'fill'">
							<lib-message
								class="push-bottom"
								[title]="'💡 Need a Hand? Just Ask the AI'"
								[type]="'tip'"
								[dismissId]="'genericAiChatInfo'"
							>
								<p>
									Our AI assistant is here to help with all kinds of coaching tasks — from programming
									ideas to client communication and beyond.
								</p>

								<p>You can ask things like:</p>
								<ul>
									<li>“Give me a 3-day strength training split for beginners”</li>
									<li>“Suggest mobility exercises for desk-bound clients”</li>
									<li>“Write a welcome message for a new online coaching client”</li>
								</ul>

								<p>
									For more personalized responses, you can also set a <strong>client context</strong>.
									This allows the AI to tailor its answers using the client’s workout history,
									nutrition, goals, and progress.
								</p>

								<p>
									Whether you're working on general plans or responding to specific clients, the AI is
									here to make your coaching smoother and faster.
								</p>
							</lib-message>

							<lib-button (click)="openClientContextSelector()" [icon]="'user'" [buttonType]="'basic'"
								>Select client context</lib-button
							>
						</div>
						}
					</div>
				</lib-card>
			</div>
		</div>
		<div class="push-top">
			<h3>Your prompt</h3>
		</div>
		<div class="push-top">
			<ui-text-area-form-field
				[maxLength]="500"
				[valueAsSignal]="form.prompt"
				[enableGenAi]="false"
				[enableEmoji]="false"
			></ui-text-area-form-field>
		</div>
		<div class="push-top">
			<ui-form-save-button
				[removeDivider]="true"
				[isInDialog]="true"
				[enableControlEnterSaveShortcut]="true"
				[title]="'Submit'"
				[saveHandler]="saveHandler()"
				[logHandler]="formLogHandler()"
				(isLoading)="isLoading.set($event)"
			></ui-form-save-button>
		</div>
		@if (isLoading()) {
		<div class="push-top">
			<div libFlexRow [libFlexRowAlign]="'center'" [libFlexRowGap]="'16px'">
				<div libFlexColumn>
					<lib-loader [loaderType]="'spinner'"></lib-loader>
				</div>
				<div libFlexColumn>
					<div libText3>Generating...</div>
				</div>
			</div>
		</div>
		} } @else { @if (promptResponse(); as promptResponse) {
		<div>
			<div [libFlexRow]="isMobileView()">
				<div libFlexColumn [libFlexSize]="isMobileView() ? 'full' : 'fill'">
					<div libText3>{{ promptSubtitle() }}</div>
				</div>
				<div libFlexColumn [libFlexSize]="isMobileView() ? 'full' : undefined">
					<lib-button (click)="handleCopyText()" [icon]="'copy'" [buttonType]="'basic'">Copy text</lib-button>
				</div>
				<div libFlexColumn [libFlexSize]="isMobileView() ? 'full' : undefined">
					<lib-button (click)="handleEditPrompt()" [icon]="'edit'" [buttonType]="'basic'"
						>Edit prompt</lib-button
					>
				</div>
			</div>

			<lib-card class="push-top-sm">
				<div [innerHTML]="promptResponse.item.generatedText | textToHtml"></div>
			</lib-card>
		</div>
		} }
	</div>
</div>

<div libFlexRow class="push-top-md">
	<div libFlexColumn [libFlexSize]="'full'">
		<lib-card [title]="'Previous prompts'">
			<lib-list
				[disableSort]="true"
				[items]="previousPromptListItems()"
				[design]="'default'"
				[pageSize]="5"
			></lib-list>
		</lib-card>
	</div>
</div>
